import {
  Autocomplete,
  Stack,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useAllCameras, useCreateRouter, useOneRouter, usePatients, useUpdateRouter } from '../../api/index.js';
import { type Camera, type Patient } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type RouterFormValues } from '../Forms/formTypes.js';
import { routerFormSchema } from '../Forms/validations.js';

const defaultValues: RouterFormValues = {
  name: '',
  imei: '',
  ipAddress: '',
  username: '',
  password: '',
  patient: {} as Patient,
  cameras: [],
};

interface RouterFormProps {
  open: boolean;
  onClose: () => void;
  routerId?: string;
  refetch: () => void;
}
const RouterForm: FC<RouterFormProps> = ({ open, routerId, refetch, onClose }) => {
  const { data } = useOneRouter(routerId!, {
    enabled: !!routerId,
  });
  const { data: camerasData = [] } = useAllCameras();
  const { data: patientsData = [] } = usePatients();

  const createMutation = useCreateRouter();
  const updateMutation = useUpdateRouter();
  const formattedData: RouterFormValues = useMemo(() => {
    if (data) {
      return {
        name: data.name,
        imei: data.imei,
        ipAddress: data.ipAddress,
        username: data.username,
        password: data.password,
        patient: data.patient,
        cameras: data.cameras,
      };
    }
    return defaultValues;
  }, [data]);

  return (
    <FormDialog
      open={open}
      name={'router'}
      onClose={onClose}
      data={formattedData}
      schema={routerFormSchema}
      isEditing={!!data}
      onSubmit={async ({ patient, cameras, ...data }, isEditing) => {
        if (isEditing) {
          await updateMutation.mutateAsync({
            id: routerId,
            ...data,
            patientId: patient?.id,
            cameraIds: cameras?.map((item: Camera) => item.id) || [],
          });
        } else {
          await createMutation.mutateAsync({
            ...data,
            patientId: patient?.id,
            cameraIds: cameras?.map((item: Camera) => item.id) || [],
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.name}
                helperText={errors?.name?.message || ''}
                label={'Name'}
              />
            )}
          />
          <Controller
            name={'imei'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.imei}
                helperText={
                  errors?.imei?.message || "The IMEI of the Router (as shown in Sentinel). Format '##-######-######-#'"
                }
                label={'IMEI'}
              />
            )}
          />
          <Controller
            name={'ipAddress'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.ipAddress}
                helperText={errors?.ipAddress?.message || 'The IP address of the Router (as shown in Sentinel)'}
                label={'IP Address'}
              />
            )}
          />
          <Controller
            name={'username'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.username}
                helperText={errors?.username?.message || ''}
                label={'Username'}
              />
            )}
          />
          <Controller
            name={'password'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.password}
                type={'password'}
                helperText={errors?.password?.message || ''}
                label={'Password'}
              />
            )}
          />
          <Controller
            name={'patient'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={!_.isEmpty(field.value) ? field.value : null}
                  onChange={(_: any, value: Patient | null) => field.onChange(value)}
                  id={'patient-select'}
                  options={patientsData}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(patient: Patient) =>
                    !_.isEmpty(patient) ? `${patient.firstName} ${patient.lastName}` : ''
                  }
                  renderInput={(params) => <TextField {...params} label={'Patient'} />}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={`${option.firstName} ${option.lastName}`} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          <Controller
            name={'cameras'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={field.value}
                  onChange={(_: any, value: Camera[] | null) => field.onChange(value)}
                  id={'cameras-select'}
                  options={camerasData}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(camera: Camera) => camera.name}
                  renderInput={(params) => <TextField {...params} label={'Cameras'} />}
                  multiple
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option.name} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default RouterForm;
